/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { FloatingOverlay, FloatingPortal } from '@floating-ui/react';
import classNames from 'classnames';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

type SpotlightProviderProps = {
  /** The app/screen content to wrap */
  children: React.ReactNode;
  /** A boolean that decides if there is a gray overlay when a spotlight is open. */
  hasOverlay?: boolean;
  /** If the overlay should be transparent */
  isOverlayTransparent?: boolean;
};

type SpotlightContextType = {
  /** A boolean that indicates if a spotlight is open */
  isOpen: boolean;
  /** A function that can be used to close or open the spotlight */
  setIsOpen: (arg: boolean) => void;
  /** A function that can be used to check if a spotlight is active. It takes a target id as an argument */
  isActiveSpotlight: (target: string) => boolean;
  /** A function that can be used to set a spotlight as active. It takes a target id as an argument, or null to clear the active spotlight */
  setActiveSpotlight: (target: string | null) => void;
  /** A function that can be used to register a target. It takes a target id, and a ref to the target element as arguments */
  registerTarget: (id: string, ref: React.RefObject<HTMLElement>) => void;
  /** A function that can be used to unregister a target. It takes a target id as an argument */
  unregisterTarget: (id: string) => void;
  /** An object that contains all registered targets. The keys are the target ids, and the values are the refs to the target elements */
  registeredTargets: Record<string, React.RefObject<HTMLElement>>;
  /** A boolean that decides if there is a gray overlay when a spotlight is open */
  hasOverlay?: boolean;
};

const SpotlightContext = createContext<SpotlightContextType | null>(null);

export const SpotlightProvider = ({
  children,
  hasOverlay = true,
  isOverlayTransparent = false,
}: SpotlightProviderProps) => {
  const [active, setActive] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [targets, setTargets] = useState<
    Record<string, React.RefObject<HTMLElement>>
  >({});
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  const registerTarget = useCallback(
    (id: string, ref: React.RefObject<HTMLElement>) => {
      setTargets((prev) => ({ ...prev, [id]: ref }));
    },
    [],
  );

  const unregisterTarget = useCallback((id: string) => {
    setTargets((prev) => {
      const newTargets = { ...prev };
      delete newTargets[id];
      return newTargets;
    });
  }, []);

  // Calculate target element position and size for mask
  useEffect(() => {
    if (
      active !== null &&
      targets[active] !== undefined &&
      targets[active]?.current !== null
    ) {
      const element = targets[active].current;
      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [active, targets]);

  const contextValue: SpotlightContextType = {
    hasOverlay,
    isActiveSpotlight: useCallback(
      (target: string) => target === active,
      [active],
    ),
    isOpen,
    registerTarget,
    registeredTargets: targets,
    setActiveSpotlight: useCallback(
      (target: string | null) => setActive(target),
      [],
    ),
    setIsOpen: useCallback((open: boolean) => setIsOpen(open), []),
    unregisterTarget,
  };

  const overlayClasses = classNames('ndl-spotlight-overlay', {
    'ndl-spotlight-overlay-opaque': !isOverlayTransparent,
    'ndl-spotlight-overlay-open': isOpen && active !== null,
  });

  const overlayRootRef = useRef<HTMLElement | null>(null);

  if (active !== null) {
    overlayRootRef.current = document.getElementById(active);
  }

  // Get the border radius from the target element
  const getHoleRadius = useCallback(() => {
    if (
      active !== null &&
      targets[active] !== undefined &&
      targets[active]?.current !== null
    ) {
      const element = targets[active].current;
      const computedStyle = window.getComputedStyle(element);
      const borderRadius = computedStyle.borderRadius;

      // Parse the border radius value (handle px, rem, em, etc.)
      const radiusValue = parseFloat(borderRadius);
      return isNaN(radiusValue) ? 0 : radiusValue;
    }
    return 0;
  }, [active, targets]);

  const getMaskStyle = () => {
    if (!targetRect || isOverlayTransparent) {
      return {};
    }

    const { left, top, width, height } = targetRect;

    const x = Math.max(0, left);
    const y = Math.max(0, top);
    const w = Math.max(0, width);
    const h = Math.max(0, height);
    const r = Math.max(0, Math.min(getHoleRadius(), Math.min(w, h) / 2));

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // SVG mask: white = visible overlay, black = hole (transparent)
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${vw}" height="${vh}" viewBox="0 0 ${vw} ${vh}">
        <defs>
          <mask id="spotlightMask" maskUnits="userSpaceOnUse">
            <rect x="0" y="0" width="${vw}" height="${vh}" fill="white"/>
            <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" ry="${r}" fill="black"/>
          </mask>
        </defs>
        <rect x="0" y="0" width="${vw}" height="${vh}" fill="black" mask="url(#spotlightMask)"/>
      </svg>`.trim();

    const encoded = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

    return {
      WebkitMaskImage: `url("${encoded}")`,
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskSize: `${vw}px ${vh}px`,
      maskImage: `url("${encoded}")`,
      maskRepeat: 'no-repeat',
      maskSize: `${vw}px ${vh}px`,
    } as React.CSSProperties;
  };

  /** useEffect to update the target rect, so that the mask tracks the target element */
  useEffect(() => {
    if (active === null) {
      return;
    }

    const el = targets[active]?.current;
    if (el === null || el === undefined) {
      return;
    }

    const update = () => setTargetRect(el.getBoundingClientRect());
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [active, targets]);

  return (
    <SpotlightContext.Provider value={contextValue}>
      {hasOverlay && active !== null && isOpen && (
        <FloatingPortal>
          <FloatingOverlay
            lockScroll={true}
            className={overlayClasses}
            style={getMaskStyle()}
            data-testid="ndl-spotlight-overlay"
          />
        </FloatingPortal>
      )}
      {children}
    </SpotlightContext.Provider>
  );
};

export const useSpotlightContext = (): SpotlightContextType => {
  const context = useContext(SpotlightContext);

  if (context === null) {
    throw new Error(
      'Spotlight components must be wrapped in <SpotlightProvider />',
    );
  }

  return context;
};
